import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GATEWAY_OFFLINE_ALERT_DEFAULT_SECONDS,
  GATEWAY_OFFLINE_ALERT_MAX_SECONDS,
  GATEWAY_OFFLINE_ALERT_MIN_SECONDS,
  type Gateway,
} from "@/contracts";

import { updateGatewayMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

const MIN_MINUTES = GATEWAY_OFFLINE_ALERT_MIN_SECONDS / 60;
const MAX_MINUTES = GATEWAY_OFFLINE_ALERT_MAX_SECONDS / 60;

/**
 * How long a gateway may stay disconnected before super admins are emailed
 * (ADR-093). Edited in minutes, stored in seconds.
 */
export function GatewayAlertDialog({
  gateway,
  onClose,
}: {
  gateway: Gateway;
  onClose: () => void;
}) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState(String(gateway.offlineAlertAfterSeconds / 60));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    ...updateGatewayMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gateways"] });
    },
  });

  async function submit() {
    const value = Number(minutes.trim());

    if (!Number.isInteger(value) || value < MIN_MINUTES || value > MAX_MINUTES) {
      setError(t("gatewayAlertMinutesInvalid", { min: MIN_MINUTES, max: MAX_MINUTES }));

      return;
    }

    setError(null);
    await mutation.mutateAsync({
      id: gateway.id,
      body: { offlineAlertAfterSeconds: value * 60 },
    });
    onClose();
  }

  return (
    <FormDialog
      open
      title={t("dialogGatewayAlertTitle")}
      description={t("dialogGatewayAlertDescription", { name: gateway.name })}
      submitLabel={t("save")}
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void submit().catch(() => undefined);
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={t("fieldGatewayAlertMinutes")}
          hint={t("fieldGatewayAlertMinutesHint", {
            default: GATEWAY_OFFLINE_ALERT_DEFAULT_SECONDS / 60,
            max: MAX_MINUTES,
          })}
          type="number"
          inputMode="numeric"
          min={MIN_MINUTES}
          max={MAX_MINUTES}
          step={1}
          required
          value={minutes}
          onChange={(event) => setMinutes(event.target.value)}
          {...(error === null ? {} : { error })}
        />
      </div>
    </FormDialog>
  );
}
